import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';

/**
 * fixedWithMore 모드: 1행 유지 + 더보기 overflow
 * - 다국어 전환 시 라벨 길이만 바뀌어도 개행 재계산 (menuLayoutKey)
 * - 메뉴 구조 변경 시에만 노출 개수 상한 리셋 (menuStructureKey)
 */
export function useFixedWithMoreLayout(menuData, themeConfig = {}) {
  const mainMenuNavRef = useRef(null);
  const moreContainerRef = useRef(null);
  const maxVisibleCapRef = useRef(null);

  const menuScrollType = themeConfig.menuScrollType || 'wrap';
  const fixedMenuCount = Math.max(1, Math.min(20, Number(themeConfig.fixedMenuCount) || 6));
  const useFixedWithMore = menuScrollType === 'fixedWithMore' && menuData && menuData.length > 0;

  const [effectiveVisibleCount, setEffectiveVisibleCount] = useState(fixedMenuCount);
  const [moreOpen, setMoreOpen] = useState(false);
  const [needReduceForLogo, setNeedReduceForLogo] = useState(false);

  const menuLayoutKey = useMemo(
    () => (menuData || []).map((item) => item.label).join('\u0001'),
    [menuData]
  );

  const menuStructureKey = useMemo(
    () => (menuData || []).map((item) => `${item.href || ''}:${(item.children || []).length}`).join('\u0001'),
    [menuData]
  );

  const maxVisibleCap = useFixedWithMore && menuData
    ? Math.min(fixedMenuCount, menuData.length)
    : fixedMenuCount;
  maxVisibleCapRef.current = maxVisibleCap;

  const effectiveVisible = useFixedWithMore
    ? Math.min(effectiveVisibleCount, menuData.length)
    : (menuData?.length ?? fixedMenuCount);
  const visibleCountToUse = useFixedWithMore && moreOpen && needReduceForLogo
    ? Math.max(1, effectiveVisible - 1)
    : effectiveVisible;
  const visibleItems = useFixedWithMore ? menuData.slice(0, visibleCountToUse) : (menuData || []);
  const overflowItems = useFixedWithMore ? menuData.slice(visibleCountToUse) : [];

  useEffect(() => {
    if (useFixedWithMore && menuData) {
      setEffectiveVisibleCount(Math.min(fixedMenuCount, menuData.length));
    }
  }, [useFixedWithMore, fixedMenuCount, menuStructureKey, menuData]);

  useEffect(() => {
    if (!useFixedWithMore || !mainMenuNavRef.current) return undefined;
    const ro = new ResizeObserver(() => {
      setEffectiveVisibleCount(maxVisibleCapRef.current);
    });
    ro.observe(mainMenuNavRef.current);
    return () => ro.disconnect();
  }, [useFixedWithMore]);

  useLayoutEffect(() => {
    if (!useFixedWithMore) return;
    setEffectiveVisibleCount(maxVisibleCapRef.current);
  }, [menuLayoutKey, useFixedWithMore]);

  const reconcileOverflowLayout = useCallback(() => {
    if (!useFixedWithMore || effectiveVisible <= 0 || !mainMenuNavRef.current) return;
    const ul = mainMenuNavRef.current.querySelector(':scope > ul.main-menu-list');
    if (!ul) return;
    const lis = ul.querySelectorAll(':scope > li');
    if (lis.length < 2) return;

    const threshold = 2;
    const firstTop = lis[0].getBoundingClientRect().top;
    let wrapIdx = -1;
    for (let i = 1; i < lis.length; i += 1) {
      if (lis[i].getBoundingClientRect().top > firstTop + threshold) {
        wrapIdx = i;
        break;
      }
    }
    if (wrapIdx < 0) return;

    const moreLi = ul.querySelector(':scope > li.main-menu-more');
    const moreIndex = moreLi ? [...lis].indexOf(moreLi) : -1;
    let newVisible;
    if (moreIndex >= 0 && wrapIdx === moreIndex) {
      newVisible = Math.max(1, moreIndex - 1);
    } else {
      newVisible = Math.max(1, wrapIdx);
    }

    if (newVisible < effectiveVisible) {
      setEffectiveVisibleCount(newVisible);
    }
  }, [useFixedWithMore, effectiveVisible]);

  useLayoutEffect(() => {
    reconcileOverflowLayout();
    const rafId = requestAnimationFrame(() => {
      reconcileOverflowLayout();
    });
    return () => cancelAnimationFrame(rafId);
  }, [
    useFixedWithMore,
    effectiveVisible,
    visibleCountToUse,
    visibleItems.length,
    overflowItems.length,
    menuLayoutKey,
    reconcileOverflowLayout,
  ]);

  useEffect(() => {
    if (!moreOpen) setNeedReduceForLogo(false);
  }, [moreOpen]);

  return {
    mainMenuNavRef,
    moreContainerRef,
    useFixedWithMore,
    showScrollButtons: menuScrollType === 'scroll',
    effectiveVisible,
    visibleCountToUse,
    visibleItems,
    overflowItems,
    moreOpen,
    setMoreOpen,
    needReduceForLogo,
    setNeedReduceForLogo,
  };
}
